// @flow
import * as React from 'react';
import config from 'Config';

import ReactAppContext from 'Context/ReactAppContext';
import { ReactPageContext } from 'Context/pageContext';
import ReactResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { getDevice } from 'Views/account/pre-payment/PrePayment.helpers';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { useRouter } from 'Client/router/useRouter';
import { CHATBOT_EVENTS, trackChatbotEvent } from './Chatbot.tracking';

import {
  createChatbot,
  createFreespeeObserver,
  createBotFuelObserver,
  setUpClickListeners,
  setUserId,
  type ObserverCell,
  type TrackingCell,
  type SendAppContextCell,
} from './Chatbot.utils';

type ChatbotProps = {
  setShowFakeBtn: (state: boolean) => void,
};

const { scriptUrl } = config.get('botfuel');
const observerCell: ObserverCell = { current: null };
const sendAppContextCell: SendAppContextCell = { current: null };
const trackingCell: TrackingCell = { open: null, close: null };

export const Chatbot = ({ setShowFakeBtn }: ChatbotProps) => {
  const { scriptLoaded: scriptsLoadedSuccessfully } = useScriptLoader(scriptUrl);
  const pageContext = React.useContext(ReactPageContext);
  const appContext = React.useContext(ReactAppContext);
  const { location } = useRouter<UrlParams, QueryParams>();
  const responsiveContext = React.useContext(ReactResponsiveContext);
  const deviceCategory = getDevice(responsiveContext).toLowerCase();

  React.useEffect(
    () => {
      if (!scriptsLoadedSuccessfully) return;

      // create the chatbot & the MutationObserver only once
      observerCell.current = createFreespeeObserver();

      setUserId(appContext.googleTrackingId).then(() => {
        // create the BotFuelObserver to track the DOM insersion of the real chatbot
        createBotFuelObserver(() => {
          trackChatbotEvent(CHATBOT_EVENTS.INITIALIZED, appContext, pageContext, location);
          setUpClickListeners({
            observerCell,
            sendAppContextCell,
            trackingCell,
            appContext,
            pageContext,
            deviceCategory,
            location,
          });
          setShowFakeBtn(false);
        });

        createChatbot();
      });
    },
    [pageContext.pageId, deviceCategory, scriptsLoadedSuccessfully],
  );

  return null;
};
